import { computed } from 'mobx';
import { ExtendedModel, getRoot, model } from  'mobx-keystone';
import NumberFormat from 'react-number-format';

import Project from "../models/Project";
import DashiCostMetric from '../../../core/metrics/Cost';
import { MetricPayload } from '../../../core/models/Metric';
import { IDashiProject, ProjectType } from '../../../core/models/Project';


@model('demo/CostMetric')
class CostMetric extends ExtendedModel(DashiCostMetric, {})  {

    @computed
    get totalsByFundingType() {
        const totals: MetricPayload[] = [];

        this.cells.forEach(cell => {
            const i = cell.period - 1;
            const { cost } = cell.value;
            const project = cell.project as Project;

            if (!totals[i]) {
                totals[i] = {};
            }

            if (!(project.funding in totals[i])) {
                totals[i][project.funding] = 0
            }

            if (cost > 0) {
                totals[i][project.funding] += project.escalatedProjectCost;
            }
        });

        return totals;
    }
    
    generatePayload(cost: number) {
        const payload: any = super.generatePayload(cost);
        payload.constructionCost = payload.cost * .71;
        return payload;
    }

    renderSettings(project: IDashiProject) {
        const { app, config } = getRoot(this);
        const { ProjectFundingColors } = config;
        const { cost, constructionCost } = project.metrics[this.name];
        const spaceTypeMetric = app.metrics['SpaceType'];
        const spaceType = project.metrics['SpaceType'];

        const projectDEMO = project as Project;

        return (
            <div className="CostMetricSettings ProjectSettings-section">
                <div className="ProjectSettings-entry">
                    <label htmlFor="ProjectFunding">Project Funding</label>
                    <select id="ProjectFunding" value={projectDEMO.funding} onChange={e => projectDEMO.setFunding(e.target.value)}>
                        {Object.keys(ProjectFundingColors).map((fund: string) => (
                            <option value={fund}>{fund}</option>
                        ))}
                    </select>
                </div>

                <div className="ProjectSettings-entry">
                    <label htmlFor="ProjectCost">Project Cost</label>
                    <div className="ProjectSettings-input">
                        <div className="ProjectSettings-entry-col">
                            <div className="labelled-input cost">
                                <label htmlFor="ProjectCost">$</label>
                                <NumberFormat
                                    id="ProjectCost"
                                    thousandSeparator={true}
                                    value={cost}
                                    onValueChange={e => project.setMetric(this.name, this.generatePayload(e.floatValue || 0))}
                                />
                            </div>

                            <div className="labelled-input readonly">
                                <label>$</label>
                                <div className="cell-value">
                                    {(
                                        project.type === ProjectType.Demo ? (
                                            projectDEMO.gsf > 0 ? cost / spaceTypeMetric.gsf(spaceType.before, projectDEMO.gsf) : 0
                                        ) : (
                                            projectDEMO.gsf > 0 ? cost / spaceTypeMetric.gsf(spaceType.after, projectDEMO.gsf) : 0
                                        )
                                    ).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                                </div>
                                <span>/GSF</span>
                            </div>
                        </div>

                        <div className="ProjectSettings-entry-col" style={{ margin: 'auto' }}>
                            in
                        </div>

                        <div className="ProjectSettings-entry-col escalation-year">
                            <div className="labelled-input">
                                <input
                                    id="EscalationYear"
                                    value={projectDEMO.escalationYear}
                                    onChange={e => projectDEMO.setEscalationYear(parseInt(e.target.value))}
                                />
                                <label htmlFor="EscalationYear">Dollars</label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="ProjectSettings-entry">
                    <label>Construction Cost</label>
                    <div className="ProjectSettings-input">
                        <div className="labelled-input readonly cost">
                            <span>$</span>
                            <div className="cell-value">
                                {Math.round(constructionCost).toLocaleString()}
                            </div>
                        </div>

                        <div className="labelled-input readonly">
                            <span>$</span>
                            <div className="cell-value">
                                {(
                                    project.type === ProjectType.Demo ? (
                                        projectDEMO.gsf > 0 ? constructionCost / spaceTypeMetric.gsf(spaceType.before, projectDEMO.gsf) : 0
                                    ) : (
                                        projectDEMO.gsf > 0 ? constructionCost / spaceTypeMetric.gsf(spaceType.after, projectDEMO.gsf) : 0
                                    )
                                ).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                            </div>
                            <span>/GSF</span>
                        </div>
                    </div>
                </div>

               <div className="ProjectSettings-entry">
                    <label htmlFor="Escalation">Escalation</label>
                    <div className="ProjectSettings-input">
                        <div className="labelled-input escalation">
                            <NumberFormat
                                id="Escalation"
                                thousandSeparator={true}
                                value={projectDEMO.escalation}
                                onValueChange={e => projectDEMO.setEscalation(e.floatValue || 0)}
                            />
                            <label htmlFor="Escalation">%</label>
                        </div>

                        <label>Compounded Escalation</label>
                        <div className="labelled-input readonly">
                            <div className="cell-value">
                                {(projectDEMO.compoundedEscalation * 100).toFixed(2)}
                            </div>
                            <span>%</span>
                        </div>
                    </div>
                </div>

               <div className="ProjectSettings-entry">
                    <label>Escalated <br/>Project Cost</label>
                    <div className="ProjectSettings-input">
                        <div className="ProjectSettings-entry-col">
                            <div className="labelled-input readonly cost">
                                <span>$</span>
                                <div className="cell-value">
                                    {Math.round(projectDEMO.escalatedProjectCost).toLocaleString()}
                                </div>
                            </div>

                            <div className="labelled-input readonly">
                                <span>$</span>
                                <div className="cell-value">
                                    {(
                                        project.type === ProjectType.Demo ? (
                                            projectDEMO.gsf > 0 ? projectDEMO.escalatedProjectCost / spaceTypeMetric.gsf(spaceType.before, projectDEMO.gsf) : 0
                                        ) : (
                                            projectDEMO.gsf > 0 ? projectDEMO.escalatedProjectCost / spaceTypeMetric.gsf(spaceType.after, projectDEMO.gsf) : 0
                                        )
                                    ).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                                </div>
                                <span>/GSF</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="ProjectSettings-entry">
                    <label>Escalated <br/>Construction Cost</label>
                    <div className="ProjectSettings-input">
                        <div className="labelled-input readonly cost">
                            <span>$</span>
                            <div className="cell-value">
                                {Math.round(projectDEMO.escalatedConstructionCost).toLocaleString()}
                            </div>
                        </div>

                        <div className="labelled-input readonly">
                            <span>$</span>
                            <div className="cell-value">
                                {(
                                    project.type === ProjectType.Demo ? (
                                        projectDEMO.gsf > 0 ? projectDEMO.escalatedConstructionCost / spaceTypeMetric.gsf(spaceType.before, projectDEMO.gsf) : 0
                                    ) : (
                                        projectDEMO.gsf > 0 ? projectDEMO.escalatedConstructionCost / spaceTypeMetric.gsf(spaceType.after, projectDEMO.gsf) : 0
                                    )
                                ).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                            </div>
                            <span>/GSF</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

}


export default CostMetric;
